import { FIRST_PARTY_APP_KEY } from '../apiConfig';

// The one IANA-zone read in the app. Login/register are the only calls that
// need it — everything after that reads the stored value back off the user.
// Wrapped: a handful of old/locked-down browsers have no Intl.DateTimeFormat,
// and a signup or sign-in must never fail because of it.
function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

export default {
  // `profile` seeds the profile node register creates (first_name, last_name).
  // `turnstileToken` is the human check's answer, sent only when there is one.
  async register(username, email, password, profile = {}, { turnstileToken } = {}) {
    return this.request('/auth/register', {
      method: 'POST',
      body: {
        username, email, password, ...profile, timezone: browserTimezone(),
        ...(turnstileToken ? { turnstile_token: turnstileToken } : {}),
      },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  // Whether a handle can be claimed: register's own rules, asked before it
  // (the join flow's handle step). {available, reason?, message?}
  async usernameAvailable(username) {
    return this.request(`/auth/username-available?username=${encodeURIComponent(username)}`, {
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  async login(identifier, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: { identifier, password, timezone: browserTimezone() },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  // PHONE-VERIFIED SIGNUP: the token is the one /auth/login answered
  // PHONE_VERIFICATION_REQUIRED with; `country` is an ISO alpha-2 hint for a
  // national number. Start sends a code; check proves it and answers exactly as
  // a successful login does.
  async phoneStart({ verificationToken, phone, country }) {
    return this.request('/auth/phone/start', {
      method: 'POST',
      body: { verification_token: verificationToken, phone, ...(country ? { country } : {}) },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  async phoneCheck({ verificationToken, phone, country, code }) {
    return this.request('/auth/phone/check', {
      method: 'POST',
      body: {
        verification_token: verificationToken, phone, code,
        ...(country ? { country } : {}), timezone: browserTimezone(),
      },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  async recoveryResetPassword(identifier, recoveryCode, newPassword, confirmPassword) {
    return this.request('/auth/password/recovery-reset', {
      method: 'POST',
      body: {
        identifier,
        recovery_code: recoveryCode,
        new_password: newPassword,
        confirm_password: confirmPassword,
      },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  async refreshToken() {
    return this.request('/auth/refresh', {
      method: 'POST',
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  // `redirectUri`: where the code is going. Required for 'admin' and 'dashboard',
  // whose codes the backend mints only for their own frontend's origin.
  async createLoginHandoffCode(audience, codeChallenge = null, redirectUri = null) {
    return this.request('/auth/handoff', {
      method: 'POST',
      body: {
        audience,
        ...(codeChallenge ? { code_challenge: codeChallenge, code_challenge_method: 'S256' } : {}),
        ...(redirectUri ? { redirect_uri: redirectUri } : {}),
      },
    });
  },

  async exchangeHandoffCode(code) {
    return this.request('/auth/handoff/exchange', {
      method: 'POST',
      body: { code },
      headers: { 'X-First-Party-Key': FIRST_PARTY_APP_KEY },
    });
  },

  async logout(scope = 'global') {
    const refreshToken = localStorage.getItem('refresh_token');
    return this.request('/auth/logout', {
      method: 'POST',
      body: { scope, ...(refreshToken ? { refresh_token: refreshToken } : {}) },
    });
  },

  // Lightweight session-liveness probe for idle polling; 401 = logged out elsewhere.
  async getSessionStatus() {
    return this.request('/auth/session/status');
  },

  // End one session anywhere — and everything minted under it (the app tokens
  // a Mac hub issued go with the hub's own sign-in).
  async revokeSession(sessionId) {
    return this.request(`/auth/sessions/${sessionId}/revoke`, { method: 'POST' });
  },

  // Mint a Paradigm session for a self-tokened app at authorize time; returns a
  // one-time { code } the app exchanges server-side for its session id (sid).
  async startAppSession(clientId, audience) {
    return this.request('/auth/session/start', {
      method: 'POST',
      body: { client_id: clientId, ...(audience ? { audience } : {}) },
    });
  },

  /**
   * Log into an app already authorized — the whole decision, server-side.
   *
   * Answers `ok` with a session code and the person's identity, or
   * `consent_required` with a reason, so /connect never has to work out whether
   * a grant is sufficient. That judgement belongs where enforcement's own
   * semantics live; recomputing it in a browser is how a person is re-asked for
   * something every other surface considers settled.
   */
  async startAppLogin({ client_id, redirect_uri, audience }) {
    // Undefined fields drop out at JSON.stringify, and the route reads an empty
    // string as absent — so there is nothing for a conditional spread to do.
    return this.request('/auth/app-session', {
      method: 'POST',
      body: { client_id, redirect_uri, audience },
    });
  },

  async getMe() {
    return this.request('/auth/me');
  },

  async getRecoveryCodes() {
    return this.request('/auth/recovery-codes');
  },

  // User Discovery
  async searchUsers(query, limit = 20) {
    const response = await this.request(`/users/search?q=${encodeURIComponent(query)}&limit=${limit}`);
    return response.users || [];
  },

  async getUser(userId) {
    return this.request(`/users/${userId}`);
  },

  async updateUser(userId, updates) {
    return this.request(`/users/${userId}`, {
      method: 'PUT',
      body: updates,
    });
  },
};
